// ============================================================
// SALT — Legal Page (Terms of Service & Privacy Policy)
// Route: /legal (bare domain only)
// Located: src/pages/LegalPage.tsx
//
// Tabbed page displaying Terms of Service and Privacy Policy.
// Supports ?tab=terms or ?tab=privacy query param for deep linking
// from registration checkboxes and footer links.
//
// Updated for compliance coverage:
// - DMCA takedown procedure (ToS §10)
// - COPPA age gate strengthened (ToS §2)
// - Arbitration clause (ToS §16)
// - CAN-SPAM (ToS §18)
// - No gambling/wagering (ToS §5)
// - GDPR lawful basis (Privacy §3)
// - GDPR data portability / export (Privacy §8)
// - CCPA rights (Privacy §9)
// - Breach notification (Privacy §7)
// - Specific data retention periods (Privacy §7)
// - Cookie classification (Privacy §5)
// - AWS DPA reference (Privacy §4)
// ============================================================

import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSeo } from '../hooks/useSeo';

import '../styles/user-profile.css';
import '../styles/legal.css';

// ── Types ───────────────────────────────────────────────────

type LegalTab = 'terms' | 'privacy';

// ── Component ───────────────────────────────────────────────

const LegalPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = (searchParams.get('tab') as LegalTab) || 'terms';
  const [activeTab, setActiveTab] = useState<LegalTab>(initialTab);

  useSeo({
    title: activeTab === 'privacy' ? 'Privacy Policy' : 'Terms of Service',
    description: 'The SALT Terms of Service and Privacy Policy.',
  });

  // ── Sync tab with query param ─────────────────────────────

  useEffect(() => {
    const tabParam = searchParams.get('tab') as LegalTab;
    if (tabParam === 'terms' || tabParam === 'privacy') {
      setActiveTab(tabParam);
    }
  }, [searchParams]);

  const handleTabChange = (tab: LegalTab) => {
    setActiveTab(tab);
    setSearchParams({ tab });
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior });
  };

  // ── Scroll to top on mount ────────────────────────────────

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior });
  }, []);

  // ── Effective date ────────────────────────────────────────

  const effectiveDate = 'September 19, 2026';
  const tosVersion = '1.3';

  // ── Render ────────────────────────────────────────────────

  return (
    <div className="sl-profile-page sl-legal-page">
      {/* ── Header ─────────────────────────────────────── */}
      <div className="sl-profile-card sl-legal-header">
        <div className="sl-legal-header-top">
          <div className="sl-legal-kicker">SALT</div>
          <div className="sl-legal-title">Legal &amp; Policies</div>
          <div className="sl-legal-subtitle">
            Please review our Terms of Service and Privacy Policy. By using
            SALT, you agree to both documents.
          </div>
        </div>

        {/* ── Tabs ──────────────────────────────────────── */}
        <div className="sl-legal-tabs">
          <button
            type="button"
            className={`sl-legal-tab ${activeTab === 'terms' ? 'sl-legal-tab--active' : ''}`}
            onClick={() => handleTabChange('terms')}
          >
            Terms of Service
          </button>
          <button
            type="button"
            className={`sl-legal-tab ${activeTab === 'privacy' ? 'sl-legal-tab--active' : ''}`}
            onClick={() => handleTabChange('privacy')}
          >
            Privacy Policy
          </button>
        </div>
      </div>

      {/* ── Content ─────────────────────────────────────── */}
      <div className="sl-profile-section">
        <div className="sl-profile-card sl-legal-content">
          <div className="sl-legal-meta">
            <span className="sl-legal-meta-item">Version {tosVersion}</span>
            <span className="sl-legal-meta-sep">·</span>
            <span className="sl-legal-meta-item">Effective {effectiveDate}</span>
          </div>

          {activeTab === 'terms' ? <TermsOfService /> : <PrivacyPolicy />}
        </div>
      </div>
    </div>
  );
};

// ── Terms of Service ──────────────────────────────────────────

const TermsOfService: React.FC = () => (
  <div className="sl-legal-body">
    <section className="sl-legal-section">
      <h2 className="sl-legal-heading">1. Introduction</h2>
      <p>
        Welcome to SALT ("we," "us," "our"), a competitive gaming
        platform operated at playsalt.gg and its associated subdomains. By
        creating an account, accessing, or using SALT (the "Platform"),
        you agree to be bound by these Terms of Service ("Terms"). If you do not
        agree to these Terms, do not create an account or use the Platform.
      </p>
      <p>
        We reserve the right to update these Terms at any time. When we do, we
        will revise the effective date and may notify you through the Platform.
        Your continued use of SALT after changes are posted constitutes
        acceptance of the updated Terms. If we make material changes, we will
        require you to re-accept these Terms upon your next login.
      </p>
    </section>

    <section className="sl-legal-section">
      <h2 className="sl-legal-heading">2. Eligibility</h2>
      <p>
        You must be at least 16 years of age to create an account and use SALT,
        regardless of your location. Users under the age of 16 are strictly
        prohibited from creating an account or using the Platform in any
        capacity. We do not knowingly collect personal information from
        children under 13, in compliance with the Children's Online Privacy
        Protection Act (COPPA).
      </p>
      <p>
        If you are between the ages of 16 and 18 (or the age of majority in your
        jurisdiction), you represent that your parent or legal guardian has
        reviewed and agrees to these Terms on your behalf.
      </p>
      <p>
        During registration, you will be required to confirm that you meet this
        age requirement. By confirming, you represent that the information you
        provide is truthful and accurate. If we discover that an account holder
        does not meet this age requirement, we will promptly terminate the
        account and delete the associated personal data.
      </p>
    </section>

    <section className="sl-legal-section">
      <h2 className="sl-legal-heading">3. Account Registration and Security</h2>
      <p>
        When you create a SALT account, you agree to the following: you
        will provide a valid email address and create a unique username. Your
        username will be publicly visible on leaderboards, team rosters, match
        histories, and user profiles across the Platform.
      </p>
      <p>
        You are responsible for maintaining the confidentiality of your login
        credentials. You must not share your account with anyone else. You are
        responsible for all activity that occurs under your account.
      </p>
      <p>
        You agree to notify us immediately if you believe your account has been
        accessed without your authorization. We reserve the right to suspend or
        terminate accounts that we reasonably believe are being shared, operated
        by automated means, or used in violation of these Terms.
      </p>
    </section>

    <section className="sl-legal-section">
      <h2 className="sl-legal-heading">4. Platform Description</h2>
      <p>
        SALT is a multi-game competitive gaming platform that provides
        organized ranked ladder competition. The Platform currently supports
        games including Halo Infinite and Halo 3, with additional games to be
        added over time. Each supported game is hosted on a dedicated subdomain.
      </p>
      <p>
        Through the Platform, users can create and join teams, participate in
        ranked matches across multiple formats (Best of 3, Best of 5, Best of
        7), track standings and leaderboard rankings, and engage with other
        players in a competitive environment.
      </p>
      <p>
        SALT is not affiliated with, endorsed by, or sponsored by the
        publishers or developers of any games featured on the Platform, including
        but not limited to Microsoft, 343 Industries, Bungie, or any other
        third-party game publisher. All game names, logos, and trademarks are
        the property of their respective owners.
      </p>
    </section>

    <section className="sl-legal-section">
      <h2 className="sl-legal-heading">5. User Conduct</h2>
      <p>
        By using SALT, you agree to conduct yourself in a manner
        consistent with fair play, sportsmanship, and respect for other users.
        You agree not to:
      </p>
      <p>
        Cheat, exploit, or use unauthorized tools or methods to gain an unfair
        advantage in any match or competition on the Platform. Manipulate match
        results, including but not limited to intentionally losing, colluding
        with opposing teams, or submitting false match reports.
      </p>
      <p>
        Harass, threaten, bully, or abuse other users through any means, whether
        on or off the Platform in connection with SALT activity. Create
        multiple accounts to circumvent bans, suspensions, cooldowns, or other
        restrictions.
      </p>
      <p>
        Impersonate any person, team, or entity, or falsely claim an affiliation
        with any person, team, or entity. Use the Platform for any unlawful
        purpose or in violation of any applicable law. Interfere with or disrupt
        the Platform, servers, or networks connected to the Platform. Attempt to
        gain unauthorized access to any portion of the Platform, other user
        accounts, or any systems or networks connected to the Platform.
      </p>
      <p>
        Use the Platform for any form of gambling, wagering, or betting activity,
        or facilitate such activity between users. SALT does not charge
        entry fees to participate in any ladder, match, or event. Any attempt to
        facilitate wagering in connection with SALT matches is strictly
        prohibited and may result in immediate account termination.
      </p>
      <p>
        From time to time, SALT may award cash or other prizes to
        participants — including, without limitation, winners of ladder
        competitions or tournaments, and winners of community-voted or judged
        recognitions (such as a "Clip of the Season" award) — at SALT's
        sole discretion. Prize winners are determined by competitive performance,
        judging, and/or community voting, and not by chance. No payment,
        purchase, or fee of any kind is required to enter, participate in, or be
        eligible for any prize. Prizes will be sent electronically (e.g., via
        Venmo) to the recipient's verified account. Recipients are solely
        responsible for any taxes owed on prizes received, and SALT may
        request tax information (e.g., a completed Form W-9) from recipients
        whose prizes meet applicable IRS reporting thresholds.
      </p>
    </section>

    <section className="sl-legal-section">
      <h2 className="sl-legal-heading">6. Teams and Matches</h2>
      <p>
        SALT allows users to create and join teams within each supported
        game. Each user may belong to one team per game per active season. Team
        captains are responsible for managing their roster, creating and
        accepting matches, and reporting match results.
      </p>
      <p>
        Match results must be reported accurately and honestly by both team
        captains. When both captains submit matching reports, results are
        confirmed and final. When captains submit conflicting reports, the match
        enters a contested state and a dispute is automatically created for
        review.
      </p>
      <p>
        Team captains may forfeit a match on behalf of their team. Forfeited
        matches are scored and points are calculated in the same manner as a
        completed match. A cooldown period of 3 hours is enforced between
        matches involving the same two teams to ensure fair competition and
        prevent result manipulation.
      </p>
    </section>

    <section className="sl-legal-section">
      <h2 className="sl-legal-heading">7. Dispute Resolution (Matches)</h2>
      <p>
        When match results are contested, SALT moderators will review
        the dispute and issue a ruling. Disputes may be resolved in favor of
        either team, declared a draw, or dismissed. All moderator decisions
        regarding disputes are final.
      </p>
      <p>
        Users who believe a ruling was made in error may contact us, but we are
        under no obligation to reverse a moderator's decision. Users involved in
        disputes are expected to cooperate with the review process. Providing
        false or misleading information during a dispute review may result in
        penalties including suspension or termination of your account.
      </p>
    </section>

    <section className="sl-legal-section">
      <h2 className="sl-legal-heading">8. Points, Rankings, and Seasons</h2>
      <p>
        SALT uses a dual-point system consisting of competitive points
        and participation points. Competitive points are awarded for wins and
        deducted for losses. Participation points are always positive and
        accumulate with each match played. Rankings are determined by display
        points, which ensure that teams are never penalized below their
        participation floor.
      </p>
      <p>
        Rankings are calculated automatically by the Platform. While we strive
        for accuracy, we do not guarantee that rankings, point calculations, or
        leaderboard positions will be free from error at all times. In the event
        of a calculation error, we will make reasonable efforts to correct it.
        Seasons have defined start and end dates. Standings and rankings reset at
        the beginning of each new season.
      </p>
    </section>

    <section className="sl-legal-section">
      <h2 className="sl-legal-heading">9. User-Generated Content</h2>
      <p>
        Users may upload content to the Platform including but not limited to
        team logos, profile avatars, team descriptions, user bios, and live
        stream links. By uploading content, you represent and warrant that you
        own the content or have the right to use and upload it, your content does
        not infringe on the intellectual property rights of any third party, and
        your content does not contain material that is obscene, defamatory,
        threatening, harassing, or otherwise objectionable.
      </p>
      <p>
        We reserve the right to review, hide, or remove any user-generated
        content at our sole discretion, with or without notice. Content that
        receives multiple reports from other users may be automatically hidden
        pending review. Uploaded images are scanned using automated content
        moderation services before they are published. Content that does not pass
        moderation will be rejected.
      </p>
      <p>
        You retain ownership of any content you upload. However, by uploading
        content to SALT, you grant us a non-exclusive, worldwide,
        royalty-free license to use, display, reproduce, and distribute that
        content solely in connection with operating and promoting the Platform.
        This license terminates when you remove the content or delete your
        account, except where your content has been shared with others and they
        have not deleted it, or where retention is necessary for the integrity of
        historical competitive records.
      </p>
    </section>

    <section className="sl-legal-section">
      <h2 className="sl-legal-heading">10. Intellectual Property and DMCA Policy</h2>
      <p>
        SALT, including its design, features, code, and original
        content, is owned by SALT and is protected by applicable
        intellectual property laws. You may not copy, modify, distribute, sell,
        or lease any part of the Platform without our prior written consent.
      </p>

      <h3 className="sl-legal-subheading">Copyright Infringement Notices (DMCA)</h3>
      <p>
        SALT respects the intellectual property rights of others and
        complies with the Digital Millennium Copyright Act (DMCA). If you believe
        that content hosted on the Platform infringes your copyright, you may
        submit a takedown notice to our designated agent. Your notice must
        include:
      </p>
      <p>
        (a) A physical or electronic signature of the copyright owner or a person
        authorized to act on their behalf. (b) Identification of the copyrighted
        work claimed to have been infringed. (c) Identification of the material
        that is claimed to be infringing, and information reasonably sufficient
        to permit us to locate the material on the Platform. (d) Your contact
        information, including your name, address, telephone number, and email
        address. (e) A statement that you have a good faith belief that the use
        of the material in the manner complained of is not authorized by the
        copyright owner, its agent, or the law. (f) A statement, made under
        penalty of perjury, that the information in the notification is accurate,
        and that you are authorized to act on behalf of the copyright owner.
      </p>
      <p>
        DMCA takedown notices should be sent to: dmca@playsalt.gg
      </p>

      <h3 className="sl-legal-subheading">Counter-Notification</h3>
      <p>
        If you believe that your content was removed or disabled by mistake or
        misidentification, you may submit a written counter-notification to our
        designated agent. Your counter-notification must include: (a) your
        physical or electronic signature, (b) identification of the content that
        was removed and the location where it appeared before removal, (c) a
        statement under penalty of perjury that you have a good faith belief the
        content was removed as a result of mistake or misidentification, and (d)
        your name, address, and telephone number, along with a statement that you
        consent to the jurisdiction of the federal court in Escambia County,
        Florida and will accept service of process from the person who submitted
        the original DMCA notice.
      </p>

      <h3 className="sl-legal-subheading">Repeat Infringers</h3>
      <p>
        In accordance with the DMCA and other applicable laws, we have adopted a
        policy of terminating, in appropriate circumstances, the accounts of
        users who are deemed to be repeat infringers. We may also, at our sole
        discretion, limit access to the Platform or terminate the account of any
        user who infringes the intellectual property rights of others, whether or
        not there is any repeat infringement.
      </p>
    </section>

    <section className="sl-legal-section">
      <h2 className="sl-legal-heading">11. Account Suspension and Termination</h2>
      <p>
        We reserve the right to suspend, restrict, or permanently ban any user
        account at our discretion for violations of these Terms, including but
        not limited to cheating or match manipulation, harassment or abuse of
        other users, uploading prohibited content, creating multiple accounts to
        evade restrictions, or any other conduct we deem harmful to the Platform
        or its community.
      </p>
      <p>
        Suspended or banned users will be notified of the action taken. Depending
        on the severity of the violation, we may issue warnings, temporary
        suspensions, or permanent bans.
      </p>
      <p>
        If you wish to delete your own account, you may do so through the
        Platform's account settings. Account deletion is subject to a 7-day
        recovery window, during which you may log back in to reactivate your
        account. After the recovery window, your account and associated data will
        be permanently deleted in accordance with our Privacy Policy.
      </p>
    </section>

    <section className="sl-legal-section">
      <h2 className="sl-legal-heading">12. Disclaimer of Warranties</h2>
      <p>
        SALT is provided on an "as is" and "as available" basis without
        warranties of any kind, whether express, implied, or statutory. We do not
        warrant that the Platform will be uninterrupted, error-free, secure, or
        free of viruses or other harmful components. We make no guarantees
        regarding the accuracy of match results, leaderboard rankings, point
        calculations, or any other data displayed on the Platform.
      </p>
    </section>

    <section className="sl-legal-section">
      <h2 className="sl-legal-heading">13. Limitation of Liability</h2>
      <p>
        To the fullest extent permitted by applicable law, SALT and its
        operators, employees, and affiliates shall not be liable for any
        indirect, incidental, special, consequential, or punitive damages arising
        out of or relating to your use of, or inability to use, the Platform.
      </p>
      <p>
        Our total liability to you for any claims arising from or related to the
        Platform shall not exceed the amount you paid to us, if any, during the
        twelve months preceding the claim. If you have not paid us anything, our
        total liability shall not exceed fifty dollars ($50.00 USD).
      </p>
    </section>

    <section className="sl-legal-section">
      <h2 className="sl-legal-heading">14. Indemnification</h2>
      <p>
        You agree to indemnify, defend, and hold harmless SALT, its
        operators, employees, and affiliates from and against any claims,
        liabilities, damages, losses, and expenses (including reasonable legal
        fees) arising out of or in any way connected with your access to or use
        of the Platform, your violation of these Terms, your violation of any
        third-party rights, or any content you upload or submit to the Platform.
      </p>
    </section>

    <section className="sl-legal-section">
      <h2 className="sl-legal-heading">15. Governing Law</h2>
      <p>
        These Terms shall be governed by and construed in accordance with the
        laws of the State of Florida, without regard to its conflict of law
        provisions.
      </p>
    </section>

    <section className="sl-legal-section">
      <h2 className="sl-legal-heading">16. Dispute Resolution and Arbitration</h2>
      <p>
        You and SALT agree that any dispute, claim, or controversy
        arising out of or relating to these Terms or the Platform (excluding
        claims for injunctive relief or intellectual property disputes) will be
        resolved through binding individual arbitration rather than in court.
        There is no judge or jury in arbitration, and court review of an
        arbitration award is limited.
      </p>
      <p>
        Arbitration shall be conducted by the American Arbitration Association
        (AAA) under its Consumer Arbitration Rules. The arbitration shall take
        place in Escambia County, Florida, or at another mutually agreed
        location. Filing, administrative, and arbitrator fees will be allocated
        as provided in the AAA Consumer Arbitration Rules, and SALT will pay
        every fee that those rules require a business to pay. Each party
        otherwise bears its own attorneys' fees and costs, unless the arbitrator
        or applicable law provides otherwise.
      </p>
      <p>
        You agree that any dispute resolution proceedings will be conducted on an
        individual basis and not in a class, consolidated, or representative
        action. If for any reason a claim proceeds in court rather than
        arbitration, both parties waive any right to a jury trial. This
        arbitration provision shall survive termination of these Terms.
      </p>
      <p>
        Notwithstanding the foregoing, either party may bring an individual
        action in small claims court for disputes within the jurisdiction of such
        court. Either party may also seek injunctive or other equitable relief in
        any court of competent jurisdiction to prevent the actual or threatened
        infringement or misappropriation of intellectual property rights.
      </p>
    </section>

    <section className="sl-legal-section">
      <h2 className="sl-legal-heading">17. Electronic Communications</h2>
      <p>
        By creating an account, you consent to receive electronic communications
        from SALT, including transactional emails (account verification,
        password resets, security alerts), platform notifications (match events,
        team activity, dispute updates), and administrative messages (Terms
        updates, policy changes, service announcements).
      </p>
      <p>
        We will not send promotional or marketing communications unless you have
        explicitly opted in to receive them. If you opt in to marketing
        communications, you may opt out at any time by following the unsubscribe
        instructions in those messages or by updating your account settings. All
        marketing communications will comply with the CAN-SPAM Act and include a
        valid physical mailing address and a clear unsubscribe mechanism.
      </p>
    </section>

    <section className="sl-legal-section">
      <h2 className="sl-legal-heading">18. Severability</h2>
      <p>
        If any provision of these Terms is found to be unenforceable or invalid
        by a court of competent jurisdiction, that provision shall be modified to
        the minimum extent necessary to make it enforceable, and the remaining
        provisions of these Terms shall remain in full force and effect.
      </p>
    </section>

    <section className="sl-legal-section">
      <h2 className="sl-legal-heading">19. Contact Information</h2>
      <p>
        If you have questions about these Terms of Service, please contact us at
        contact@playsalt.gg, via the SALT Discord server, or through
        the contact information provided on the Platform.
      </p>
    </section>

    <div className="sl-legal-footer-note">
      By creating an account on SALT, you acknowledge that you have
      read, understood, and agree to be bound by these Terms of Service.
    </div>
  </div>
);

// ── Privacy Policy ───────────────────────────────────────────

const PrivacyPolicy: React.FC = () => (
  <div className="sl-legal-body">
    <section className="sl-legal-section">
      <h2 className="sl-legal-heading">1. Introduction</h2>
      <p>
        SALT ("we," "us," "our") operates the competitive gaming
        platform located at playsalt.gg and its associated subdomains (the
        "Platform"). This Privacy Policy describes how we collect, use, store,
        and protect your personal information when you use our Platform.
      </p>
      <p>
        By creating an account or using SALT, you consent to the
        practices described in this Privacy Policy. If you do not agree with this
        Privacy Policy, please do not use the Platform.
      </p>
    </section>

    <section className="sl-legal-section">
      <h2 className="sl-legal-heading">2. Information We Collect</h2>

      <h3 className="sl-legal-subheading">Information You Provide Directly</h3>
      <p>
        When you register for an account, we collect your email address,
        username, and password. Your password is managed through our
        authentication provider (AWS Cognito) and is never stored in plaintext
        by SALT.
      </p>
      <p>
        When you use the Platform, you may choose to provide additional
        information including a profile bio, a profile avatar image, social media
        links (Twitch, YouTube, Twitter/X, Discord, Steam), in-game names for
        supported games, team names, team tags, team descriptions, and team logo
        images. This information is provided voluntarily and is used to enhance
        your experience on the Platform and to display your profile to other
        users.
      </p>

      <h3 className="sl-legal-subheading">Information Collected Automatically</h3>
      <p>
        When you access SALT, we automatically collect certain technical
        information including your IP address, browser type and version, device
        information, pages visited and features used, timestamps of your
        interactions with the Platform, and authentication tokens stored as
        cookies necessary for keeping you logged in.
      </p>

      <h3 className="sl-legal-subheading">Information Generated Through Platform Use</h3>
      <p>
        As you participate on the Platform, we generate and store records of your
        activity including match results and match history, team membership and
        team history, leaderboard rankings and point totals, dispute involvement
        and outcomes, content reports you submit or that are submitted about your
        content, notifications sent to your account, and live stream declarations
        during active matches.
      </p>
    </section>

    <section className="sl-legal-section">
      <h2 className="sl-legal-heading">3. How We Use Your Information and Legal Basis</h2>
      <p>
        We use the information we collect to operate the Platform, including
        creating and managing your account, processing match results and updating
        leaderboard standings, managing team creation, invitations, and roster
        changes, facilitating dispute resolution, sending notifications about
        match events, team activity, and administrative actions, and displaying
        your profile, team affiliations, and competitive history to other users.
      </p>
      <p>
        We also use activity data to maintain the integrity of competition by
        detecting and preventing cheating, multi-accounting, match manipulation,
        and other forms of abuse. Images uploaded to the Platform are scanned
        using automated content moderation services before they are published.
      </p>
      <p>
        We may analyze aggregate, anonymized usage data to understand how the
        Platform is used and to improve its features and performance. We will
        only send promotional or marketing communications if you have opted in to
        receive them, and you may opt out at any time.
      </p>

      <h3 className="sl-legal-subheading">Legal Basis for Processing (EEA/UK Users)</h3>
      <p>
        If you are located in the European Economic Area (EEA) or United Kingdom,
        we rely on the following legal bases under the General Data Protection
        Regulation (GDPR) for processing your personal data:
      </p>
      <p>
        <strong>Performance of a contract:</strong> Processing your account
        information, match data, team memberships, and leaderboard standings is
        necessary to provide you with the competitive gaming services described
        in our Terms of Service.
      </p>
      <p>
        <strong>Legitimate interests:</strong> We process data for security
        purposes (fraud detection, multi-account prevention, rate limiting),
        platform integrity (dispute resolution, content moderation), and service
        improvement (aggregate analytics). We have balanced these interests
        against your rights and determined they are proportionate.
      </p>
      <p>
        <strong>Consent:</strong> Where we process data based on your consent
        (such as optional profile information you choose to provide, or marketing
        communications you opt in to), you have the right to withdraw consent at
        any time. Withdrawing consent does not affect the lawfulness of
        processing carried out before the withdrawal.
      </p>
      <p>
        <strong>Legal obligation:</strong> We may process your data where
        necessary to comply with applicable laws, regulations, or legal
        processes.
      </p>
    </section>

    <section className="sl-legal-section">
      <h2 className="sl-legal-heading">4. How We Share Your Information</h2>

      <h3 className="sl-legal-subheading">Publicly Visible Information</h3>
      <p>
        By design, certain information on SALT is visible to all users
        and visitors. This includes your username, profile avatar, profile bio,
        social media links, in-game names, team memberships and roles, match
        history and results, leaderboard ranking and point totals, and live
        stream links during active matches. This transparency is necessary for
        competitive gaming platforms and you consent to this information being
        publicly accessible by using the Platform.
      </p>

      <h3 className="sl-legal-subheading">Service Providers (Data Processors)</h3>
      <p>
        We use third-party service providers to help operate the Platform. These
        providers process your data on our behalf and are bound by contractual
        obligations to protect your information. Our primary service providers
        include:
      </p>
      <p>
        <strong>Amazon Web Services (AWS):</strong> Our infrastructure, including
        servers, databases, file storage, and authentication services (AWS
        Cognito), is hosted on AWS. AWS processes your data in accordance with
        their Data Processing Addendum (DPA), which is incorporated into the AWS
        Customer Agreement and meets the requirements of GDPR Article 28 for
        data processor agreements. AWS data centers used by SALT are
        located in the United States (us-east-1 region).
      </p>
      <p>
        We do not sell, rent, or trade your personal information to any third
        party for their marketing purposes. We do not share your personal data
        with any third parties other than the service providers described above,
        except as required by law.
      </p>

      <h3 className="sl-legal-subheading">Legal Requirements</h3>
      <p>
        We may disclose your information if required to do so by law, regulation,
        legal process, or governmental request, or if we believe in good faith
        that disclosure is necessary to protect the rights, property, or safety
        of SALT, our users, or the public.
      </p>
    </section>

    <section className="sl-legal-section">
      <h2 className="sl-legal-heading">5. Cookies and Tracking Technologies</h2>
      <p>
        SALT uses a limited number of cookies to operate the Platform.
        We categorize them as follows:
      </p>

      <h3 className="sl-legal-subheading">Strictly Necessary Cookies</h3>
      <p>
        <strong>Authentication cookie (sl_auth_token):</strong> When you log in,
        a session cookie is set to maintain your authentication across the
        Platform, including across game-specific subdomains. This cookie contains
        a digitally signed token that identifies your session. It is classified as
        "strictly necessary" under applicable cookie laws, meaning it is
        essential for the Platform to function and does not require separate
        consent.
      </p>
      <p>
        <strong>Cookie consent preference (sl_cookie_consent):</strong> This
        cookie stores your choice regarding analytics cookies (accepted or
        declined). It is also classified as strictly necessary because it is
        required to respect your privacy preferences.
      </p>

      <h3 className="sl-legal-subheading">Analytics Cookies (Consent Required)</h3>
      <p>
        We use Google Analytics, a web analytics service provided by Google LLC,
        to help us understand how the Platform is used. Google Analytics uses
        cookies (including _ga, _ga_*, and _gid) to collect information about
        your interactions with the Platform, such as pages visited, time spent,
        and general navigation patterns.
      </p>
      <p>
        Analytics cookies are only set after you provide your consent through
        the cookie consent banner displayed when you first visit the Platform.
        If you decline analytics cookies, Google Analytics will not be loaded and
        no analytics data will be collected. You may change your preference at
        any time by clearing your cookies and revisiting the Platform.
      </p>
      <p>
        When analytics cookies are enabled, we configure Google Analytics with
        IP anonymization, meaning your full IP address is not stored by Google.
        Google processes analytics data in accordance with the{' '}
        <a
          href="https://policies.google.com/privacy"
          target="_blank"
          rel="noopener noreferrer"
          className="sl-legal-link"
        >
          Google Privacy Policy
        </a>
        . You can also opt out of Google Analytics across all websites by
        installing the{' '}
        <a
          href="https://tools.google.com/dlpage/gaoptout"
          target="_blank"
          rel="noopener noreferrer"
          className="sl-legal-link"
        >
          Google Analytics Opt-out Browser Add-on
        </a>
        .
      </p>

      <h3 className="sl-legal-subheading">No Advertising or Third-Party Tracking Cookies</h3>
      <p>
        We do not use cookies for advertising, behavioral profiling, or
        retargeting. We do not allow third-party advertisers to set cookies on
        the Platform. The only third-party cookies present are those set by
        Google Analytics, and only with your explicit consent.
      </p>
      <p>
        You may configure your browser to refuse all cookies, but doing so will
        prevent you from logging in and using authenticated features of the
        Platform.
      </p>
    </section>

    <section className="sl-legal-section">
      <h2 className="sl-legal-heading">6. Data Storage and Security</h2>
      <p>
        Your data is stored on servers hosted by Amazon Web Services in the
        United States (us-east-1 region). We implement reasonable technical and
        organizational measures to protect your personal information, including
        encrypted data transmission (HTTPS/TLS), secure password management
        through AWS Cognito (passwords are hashed and never stored in plaintext),
        HTTP-only authentication cookies with Secure and SameSite flags, input
        validation and sanitization on all user inputs, rate limiting to protect
        against abuse and automated attacks, role-based access controls for
        administrative functions, automated content moderation for uploaded
        images, and an audit log of all administrative actions.
      </p>
      <p>
        While we take reasonable precautions, no method of electronic
        transmission or storage is completely secure. We cannot guarantee the
        absolute security of your data.
      </p>
    </section>

    <section className="sl-legal-section">
      <h2 className="sl-legal-heading">7. Data Retention and Breach Notification</h2>

      <h3 className="sl-legal-subheading">Retention Periods</h3>
      <p>
        We retain your personal information for as long as your account is active
        or as needed to provide you with the Platform's services. Specific
        retention periods are as follows:
      </p>
      <p>
        <strong>Account data</strong> (email, username, profile information):
        Retained while your account is active. Upon account deletion, a 7-day
        recovery window begins during which your account is soft-deleted. You may
        log in during this window to reactivate your account. After the recovery
        window expires, your personal data is permanently deleted or anonymized.
      </p>
      <p>
        <strong>Competitive records</strong> (match results, team participation,
        leaderboard history): Retained in anonymized or de-identified form after
        account deletion to maintain the integrity of historical season records.
        Anonymized records use placeholder identifiers and cannot be linked back
        to your identity.
      </p>
      <p>
        <strong>Notifications:</strong> Automatically deleted 30 days after
        creation.
      </p>
      <p>
        <strong>Rate limiting data:</strong> Automatically purged daily.
      </p>
      <p>
        <strong>Server logs:</strong> Retained for no more than 90 days for
        security and debugging purposes.
      </p>
      <p>
        <strong>Content reports:</strong> Retained for the duration of the review
        process and for a reasonable period thereafter for audit purposes.
      </p>

      <h3 className="sl-legal-subheading">Data Breach Notification</h3>
      <p>
        In the event of a data breach that compromises the security,
        confidentiality, or integrity of your personal data, we will take the
        following steps: We will investigate the breach promptly and take
        measures to contain it and mitigate any harm. If the breach is likely to
        result in a risk to your rights and freedoms, we will notify affected
        users without undue delay via email and/or through the Platform. Where
        required by law (including GDPR Article 33), we will notify the relevant
        supervisory authority within 72 hours of becoming aware of the breach. We
        will document the breach, its effects, and the remedial actions taken.
      </p>
    </section>

    <section className="sl-legal-section">
      <h2 className="sl-legal-heading">8. Your Rights and Choices</h2>
      <p>
        You can view and update your profile information, bio, avatar, social
        links, and in-game names directly through the Platform at any time. You
        may delete your account at any time through the account settings, subject
        to the 7-day recovery window described above.
      </p>

      <h3 className="sl-legal-subheading">Rights for All Users</h3>
      <p>
        Regardless of your location, you have the right to access and update the
        personal information we hold about you through your account settings, to
        delete your account and personal data, and to opt out of any marketing
        communications.
      </p>

      <h3 className="sl-legal-subheading">
        Additional Rights for EEA/UK Users (GDPR)
      </h3>
      <p>
        If you are located in the European Economic Area or the United Kingdom,
        you have additional rights under the General Data Protection Regulation,
        including:
      </p>
      <p>
        <strong>Right of access:</strong> You may request a copy of the personal
        data we hold about you.
      </p>
      <p>
        <strong>Right to rectification:</strong> You may request that we correct
        inaccurate or incomplete personal data.
      </p>
      <p>
        <strong>Right to erasure:</strong> You may request that we delete your
        personal data, subject to certain exceptions (such as data retained for
        legal obligations or the integrity of historical competitive records).
      </p>
      <p>
        <strong>Right to restrict processing:</strong> You may request that we
        restrict the processing of your personal data in certain circumstances.
      </p>
      <p>
        <strong>Right to data portability:</strong> You may request a copy of
        your personal data in a structured, commonly used, machine-readable
        format (such as JSON). This includes your profile information, match
        history, team memberships, and other personal data you have provided to
        us.
      </p>
      <p>
        <strong>Right to object:</strong> You may object to processing based on
        legitimate interests. We will cease processing unless we demonstrate
        compelling legitimate grounds.
      </p>
      <p>
        <strong>Right to lodge a complaint:</strong> You have the right to lodge
        a complaint with a supervisory authority in your country of residence if
        you believe your data protection rights have been violated.
      </p>
      <p>
        To exercise any of these rights, please contact us at
        contact@playsalt.gg. We will respond to your request within 30 days,
        or within the timeframe required by applicable law.
      </p>
    </section>

    <section className="sl-legal-section">
      <h2 className="sl-legal-heading">
        9. Additional Rights for California Users (CCPA/CPRA)
      </h2>
      <p>
        If you are a California resident, you may have additional rights under
        the California Consumer Privacy Act (CCPA) and the California Privacy
        Rights Act (CPRA), including:
      </p>
      <p>
        <strong>Right to know:</strong> You may request information about the
        categories and specific pieces of personal information we have collected
        about you, the categories of sources from which it was collected, the
        business purpose for collecting it, and the categories of third parties
        with whom we share it.
      </p>
      <p>
        <strong>Right to delete:</strong> You may request deletion of your
        personal information, subject to certain exceptions.
      </p>
      <p>
        <strong>Right to opt out of sale:</strong> We do not sell your personal
        information. We do not share your personal information with third parties
        for cross-context behavioral advertising.
      </p>
      <p>
        <strong>Right to non-discrimination:</strong> We will not discriminate
        against you for exercising any of your CCPA/CPRA rights.
      </p>
      <p>
        To exercise your California privacy rights, please contact us at
        contact@playsalt.gg. We will verify your identity before fulfilling
        your request.
      </p>
    </section>

    <section className="sl-legal-section">
      <h2 className="sl-legal-heading">10. Children's Privacy</h2>
      <p>
        SALT is not intended for children under the age of 16; see Terms of
        Service &sect;2 (Eligibility) for our platform-wide minimum age
        requirement. We do not knowingly collect personal information from
        children under 13, in compliance with the Children's Online Privacy
        Protection Act (COPPA). Our registration process requires users to
        confirm they meet the minimum age requirement before an account can be
        created.
      </p>
      <p>
        If we become aware that we have collected information from a user under
        our minimum age requirement, we will take steps to delete that
        information and terminate the associated account promptly. If you are a
        parent or guardian and believe your child has provided us with personal
        information in violation of this policy, please contact us at
        contact@playsalt.gg so we can take appropriate action.
      </p>
    </section>

    <section className="sl-legal-section">
      <h2 className="sl-legal-heading">11. International Data Transfers</h2>
      <p>
        SALT is operated from the United States. If you access the
        Platform from outside the United States, please be aware that your
        information will be transferred to, stored, and processed in the United
        States.
      </p>
      <p>
        If you are located in the European Economic Area (EEA), United Kingdom,
        or another jurisdiction with data protection laws that restrict
        international data transfers, your data will be transferred to the
        United States based on the following safeguards: AWS maintains compliance
        with applicable data transfer frameworks including the EU-U.S. Data
        Privacy Framework, and processing is carried out under AWS's Data
        Processing Addendum which includes Standard Contractual Clauses (SCCs)
        approved by the European Commission.
      </p>
      <p>
        By using the Platform, you acknowledge and consent to the transfer of
        your information to the United States as described in this policy.
      </p>
    </section>

    <section className="sl-legal-section">
      <h2 className="sl-legal-heading">12. Changes to This Privacy Policy</h2>
      <p>
        We may update this Privacy Policy from time to time to reflect changes in
        our practices, technology, or legal requirements. When we make material
        changes, we will update the effective date and may notify you through the
        Platform. We encourage you to review this Privacy Policy periodically.
        Material changes to data processing practices will require re-acceptance
        upon your next login.
      </p>
    </section>

    <section className="sl-legal-section">
      <h2 className="sl-legal-heading">13. Contact Us</h2>
      <p>
        If you have questions, concerns, or requests regarding this Privacy
        Policy or our data practices, please contact us at:
      </p>
      <p>
        <strong>Privacy inquiries and data requests:</strong> contact@playsalt.gg
      </p>
      <p>
        You may also contact us via the SALT Discord server or through
        the contact information provided on the Platform.
      </p>
    </section>

    <div className="sl-legal-footer-note">
      By creating an account on SALT, you acknowledge that you have
      read, understood, and agree to this Privacy Policy.
    </div>
  </div>
);

export default LegalPage;